import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';

/**
 * A small anchored panel: menus, column pickers, multi-selects.
 *
 * Deliberately not a modal — it does not trap focus or block the page, because these
 * are quick side choices in the middle of a task. It does close on Escape, on an
 * outside click, and when the anchor scrolls out from under it, and it flips to the
 * left edge when it would otherwise run off a narrow screen.
 */

export interface PopoverProps {
  /** The clickable thing the panel hangs off. */
  trigger: (props: {
    ref: RefObject<HTMLButtonElement | null>;
    onClick: () => void;
    'aria-expanded': boolean;
    'aria-haspopup': 'menu' | 'dialog' | 'listbox';
    'aria-controls': string | undefined;
    open: boolean;
  }) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'start' | 'end';
  role?: 'menu' | 'dialog' | 'listbox';
  /** Panel width. `trigger` matches the trigger's own width. */
  width?: number | 'trigger' | 'auto';
  className?: string;
  panelClassName?: string;
  onOpenChange?: (open: boolean) => void;
}

export function Popover({
  trigger,
  children,
  align = 'start',
  role = 'menu',
  width = 'auto',
  className,
  panelClassName,
  onOpenChange,
}: PopoverProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  /*
   * The panel's viewport position. The panel is PORTALED to <body> and fixed,
   * because rendered inline it inherits every ancestor's clipping: the filter
   * bars sit inside `overflow-hidden` cards, and the owner met a dropdown that
   * "doesn't open" — it opened two millimetres and was clipped away.
   */
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const change = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  const close = useCallback(() => {
    change(false);
    // Send focus back where it came from, or the keyboard user is stranded.
    triggerRef.current?.focus();
  }, [change]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      change(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close, change]);

  /*
   * Compute where the panel sits, in viewport coordinates. Opens upwards when
   * there is not enough room below — a filter row at the bottom of a long table
   * would otherwise drop its panel off the viewport — and always hands the panel
   * a max-height so a long option list scrolls inside itself instead of running
   * off the screen.
   */
  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      // The anchor is gone (unmounted, display:none): nothing to hang off.
      change(false);
      return;
    }
    const dropUp = window.innerHeight - rect.bottom < 280 && rect.top > 320;
    const style: CSSProperties = {};
    if (dropUp) {
      style.bottom = window.innerHeight - rect.top + 4;
      style.maxHeight = Math.min(rect.top - 16, 420);
    } else {
      style.top = rect.bottom + 4;
      style.maxHeight = Math.min(window.innerHeight - rect.bottom - 16, 420);
    }
    const assumedWidth =
      typeof width === 'number' ? width : Math.max(192, width === 'trigger' ? rect.width : 192);
    if (align === 'end') {
      style.right = Math.max(8, window.innerWidth - rect.right);
    } else {
      style.left = Math.max(8, Math.min(rect.left, window.innerWidth - 8 - assumedWidth));
    }
    if (width === 'trigger') style.minWidth = rect.width;
    else if (typeof width === 'number') style.width = width;
    setPos(style);
  }, [align, width, change]);

  useLayoutEffect(() => {
    if (open) place();
    else setPos(null);
  }, [open, place]);

  // Fixed positioning does not follow the page, so the panel re-anchors on any
  // scroll (capture: inner scrollers too) and on resize.
  useEffect(() => {
    if (!open) return;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  return (
    <div className={clsx('relative inline-flex', className)}>
      {trigger({
        ref: triggerRef,
        onClick: () => change(!open),
        'aria-expanded': open,
        'aria-haspopup': role,
        'aria-controls': open ? id : undefined,
        open,
      })}

      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              id={id}
              role={role}
              style={pos}
              className={clsx(
                /*
                 * Above the drawer (z-100), below the toast (z-200). Below
                 * 1024px every filter moves into a Drawer, and at z-50 this
                 * panel painted BEHIND it: tapping Model, Variant or Colour on
                 * a phone did nothing visible, while the same control worked on
                 * a desktop (owner, 16 Sep 2026 — "some thing are not visible
                 * on the phone view which are visible on the desktop").
                 */
                'fixed z-150 flex min-w-[12rem] flex-col overflow-y-auto rounded-md border border-line bg-surface shadow-float',
                'animate-slide-up',
                'max-w-[min(22rem,calc(100vw-2rem))]',
                panelClassName,
              )}
            >
              {children(close)}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/** A row inside a popover menu. */
export function MenuItem({
  icon,
  children,
  onClick,
  disabled = false,
  destructive = false,
  shortcut,
  className,
}: {
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  shortcut?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      data-focus-inset
      className={clsx(
        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-micro',
        'transition-colors duration-100',
        disabled
          ? 'cursor-not-allowed text-faint'
          : destructive
            ? 'text-danger hover:bg-[color-mix(in_oklab,var(--c-danger)_10%,transparent)]'
            : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
        className,
      )}
    >
      {icon ? <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">{icon}</span> : null}
      <span className="flex-1 truncate">{children}</span>
      {shortcut ? <span className="font-data text-label text-faint">{shortcut}</span> : null}
    </button>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-line" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pt-2 pb-1 text-label font-semibold tracking-wide text-faint uppercase">
      {children}
    </div>
  );
}
